import { useCallback, useEffect, useRef, useState } from 'react';

export function useUniverseSound() {
  const [enabled, setEnabled] = useState(() => { try { return localStorage.getItem('universe-sound') !== 'off'; } catch { return true; } });
  const audio = useRef<AudioContext | null>(null);
  const recordings = useRef<Partial<Record<'shuffle' | 'deal', AudioBuffer>>>({});
  const playing = useRef(new Set<AudioBufferSourceNode>());
  useEffect(() => {
    if (!enabled) { playing.current.forEach(source => { try { source.stop(); } catch {} }); return; }
    let cancelled = false;
    try {
      const context = audio.current && audio.current.state !== 'closed' ? audio.current : new AudioContext(); audio.current = context;
      (['shuffle', 'deal'] as const).forEach(kind => {
        if (recordings.current[kind]) return;
        void fetch(`${process.env.PUBLIC_URL || ''}/audio/universe/card-${kind}.wav`)
          .then(response => { if (!response.ok) throw new Error('Audio unavailable'); return response.arrayBuffer(); })
          .then(data => context.decodeAudioData(data))
          .then(buffer => { if (!cancelled) recordings.current[kind] = buffer; })
          .catch(() => { /* Keep optional audio failures silent. */ });
      });
    } catch { /* Web Audio is optional. */ }
    return () => { cancelled = true; };
  }, [enabled]);
  useEffect(() => () => { const context = audio.current; audio.current = null; void context?.close().catch(() => {}); }, []);
  const play = useCallback((kind: 'travel' | 'deal' | 'shuffle' | 'flip' | 'activate' = 'travel') => {
    if (!enabled) return;
    try {
      const context = audio.current && audio.current.state !== 'closed' ? audio.current : new AudioContext(); audio.current = context;
      void context.resume().catch(() => {});
      if (kind === 'shuffle' || kind === 'deal') {
        const buffer = recordings.current[kind];
        if (buffer) {
          const source = context.createBufferSource(); source.buffer = buffer;
          const gain = context.createGain();
          const now = context.currentTime;
          const duration = kind === 'shuffle' ? .34 : buffer.duration;
          gain.gain.setValueAtTime(kind === 'shuffle' ? .7 : .65, now);
          gain.gain.setValueAtTime(kind === 'shuffle' ? .7 : .65, now + Math.max(0, duration - .025));
          gain.gain.linearRampToValueAtTime(0, now + duration);
          source.connect(gain); gain.connect(context.destination);
          playing.current.add(source);
          source.onended = () => { playing.current.delete(source); source.disconnect(); gain.disconnect(); };
          source.start(now); source.stop(now + duration);
          return;
        }
        // Never play a late shuffle when a recording finishes loading.
        if (kind === 'shuffle') return;
      }
      if (kind === 'activate') {
        // A soft rising major chord, followed by a lingering bell-like shimmer.
        [261.63, 392, 523.25, 783.99].forEach((frequency, index) => {
          const start = context.currentTime + index * .12;
          const oscillator = context.createOscillator();
          const gain = context.createGain();
          oscillator.type = 'sine';
          oscillator.frequency.setValueAtTime(frequency, start);
          gain.gain.setValueAtTime(.0001, start);
          gain.gain.exponentialRampToValueAtTime(.065, start + .035);
          gain.gain.exponentialRampToValueAtTime(.0001, start + 1.15);
          oscillator.connect(gain); gain.connect(context.destination);
          oscillator.start(start); oscillator.stop(start + 1.2);
          oscillator.onended = () => { oscillator.disconnect(); gain.disconnect(); };
        });
        return;
      }
      if (kind === 'deal') {
        const buffer = context.createBuffer(1, Math.ceil(context.sampleRate * .075), context.sampleRate);
        const samples = buffer.getChannelData(0);
        for (let i = 0; i < samples.length; i++) samples[i] = (Math.random() * 2 - 1) * (1 - i / samples.length);
        const source = context.createBufferSource(); source.buffer = buffer;
        const filter = context.createBiquadFilter(); filter.type = 'lowpass'; filter.frequency.value = 1500;
        const level = context.createGain(); level.gain.value = .09;
        source.connect(filter); filter.connect(level); level.connect(context.destination); source.start();
        source.onended = () => { source.disconnect(); filter.disconnect(); level.disconnect(); };
        return;
      }
      const oscillator = context.createOscillator(); const gain = context.createGain();
      oscillator.type = 'sine'; oscillator.frequency.setValueAtTime(kind === 'flip' ? 540 : 280, context.currentTime);
      oscillator.frequency.exponentialRampToValueAtTime(kind === 'travel' ? 440 : 220, context.currentTime + .15);
      gain.gain.setValueAtTime(.0001, context.currentTime); gain.gain.exponentialRampToValueAtTime(.085, context.currentTime + .02); gain.gain.exponentialRampToValueAtTime(.0001, context.currentTime + .18);
      oscillator.connect(gain); gain.connect(context.destination); oscillator.start(); oscillator.stop(context.currentTime + .2);
      oscillator.onended = () => { oscillator.disconnect(); gain.disconnect(); };
    } catch { /* Audio is optional when unavailable or blocked by the browser. */ }
  }, [enabled]);
  return { enabled, play, toggle: () => setEnabled(value => { try { localStorage.setItem('universe-sound', value ? 'off' : 'on'); } catch {} return !value; }) };
}
